import _ from 'lodash';
import React from 'react';

import Loader from 'components/Loader';
import InvoiceStatusTag from 'components/InvoiceStatusTag';
import {default as Modal, withModal} from 'components/Modal';
import VisibleIf from 'components/VisibleIf';
import ExternalLink from 'components/ExternalLink';

import {
  BaseInformation,
  InvoiceItemsTable,
  Totals,
  LessSection,
  AmountDue,
  ReceivePayment
} from './components';

import {
  MODALS,
  SCHEDULE_ITEM_TYPES,
  ACCOUNT_TYPES,
  SCHEDULE_TYPES,
  FETCH_SCHEDULE_DELAY,
  fetchInvoice,
  voidObject,
  addXeroId,
  syncWithXero,
  fetchXeroAccounts,
  deallocateCreditNotes,
  fetchInvoiceAllocatedCreditNotesXeroLinks
} from 'ducks/tenancy';
import {delayedAction, composeActions} from 'ducks/common';
import {
  MODALS as BASE_MODALS,
  openModal,
  closeModal,
  storeDataInModal
} from 'ducks/modals';
import {requiresPermission, PERMISSIONS} from 'ducks/auth';

import './styles.css';

class InvoiceDetailModal extends React.Component<any> {
  componentDidMount() {
    const {data: {invoiceId, locationRef, tenancyId}} = this.props;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_SCHEDULE_ITEM_DETAILS, locationRef},
      () => {
        const fetchInvoiceExtraAction = this.props.storeDataExtraActions(
          data => ({
            invoice: data
          })
        );

        this.props.fetchInvoice({invoiceId}, fetchInvoiceExtraAction);

        const fetchInvoiceAllocationWarningsAction = this.props.storeDataExtraActions(
          data => ({
            invoiceAllocationWarnings: data
          })
        );

        this.props.fetchInvoiceAllocatedCreditNotesXeroLinks(
          {invoiceId, tenancyId},
          fetchInvoiceAllocationWarningsAction
        );

        const fetchXeroAccountsExtraActions = this.props.storeDataExtraActions(
          data => ({xeroAccounts: data})
        );
        this.props.fetchXeroAccounts(
          {
            accountType: ACCOUNT_TYPES.BANK,
            location: locationRef
          },
          fetchXeroAccountsExtraActions
        );
      }
    );
  }

  openConfirmationModal = ({heading, confirmAction, question, warnings}) =>
    this.props.openModal(BASE_MODALS.CONFIRM, {
      heading,
      question,
      confirmAction,
      warnings
    });

  getSuccessExtraActions = () => {
    return [
      () => closeModal(MODALS.CONFIRM_PAYMENT),
      ...this.props.closeModalExtraActions,
      ...this.props.refetchExtraActions({
        tenancyId: this.props.data.tenancyId,
        scheduleType: this.props.data.scheduleType
      })
    ];
  };

  delayedFetchScheduleAction = () =>
    delayedAction({
      delay: FETCH_SCHEDULE_DELAY,
      action: () =>
        composeActions(
          this.props.delayExtraActions({
            tenancyId: this.props.data.tenancyId,
            scheduleType: this.props.data.scheduleType
          })
        )
    });

  voidInvoiceConfirmAction = () => {
    const successExtraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      ...this.getSuccessExtraActions(),
      this.delayedFetchScheduleAction
    ];
    const failureExtraAction = [
      ({errors}) =>
        storeDataInModal({
          modalName: BASE_MODALS.CONFIRM,
          data: {errors, submitting: false}
        })
    ];

    this.props.voidObject(
      {
        objectId: this.props.data.invoiceId,
        objectType: SCHEDULE_ITEM_TYPES.INVOICE,
        scheduleType: this.props.data.scheduleType,
        tenancyId: this.props.data.tenancyId
      },
      successExtraActions,
      failureExtraAction
    );
  };

  voidInvoice = () =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.VOID_SCHEDULE_ITEMS
      },
      () =>
        this.openConfirmationModal({
          heading: 'Void Invoice',
          question: 'Are you sure you want to void this invoice?',
          warnings: [],
          confirmAction: this.voidInvoiceConfirmAction
        })
    );

  deallocateCreditNotesConfirmAction = () => {
    const successExtraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      ...this.getSuccessExtraActions(),
      this.delayedFetchScheduleAction
    ];
    const failureExtraAction = [
      ({errors}) =>
        storeDataInModal({
          modalName: BASE_MODALS.CONFIRM,
          data: {errors, submitting: false}
        })
    ];

    this.props.deallocateCreditNotes(
      {
        invoiceId: this.props.data.invoiceId,
        tenancyId: this.props.data.tenancyId
      },
      successExtraActions,
      failureExtraAction
    );
  };

  deallocateCreditNotes = () => {
    const {warnings} = this.props.data;
    const modal_warnings = (
      <div>
        <div>
          Due to limitations of the Xero API, you must manually deallocate any
          allocated credit note from the invoice in the Xero user interface and
          then deallocate it from Colab.
        </div>
        <div>Please, follow the instructions:</div>
        <div>1) Click on every credit note detail link listed below.</div>
        <div>
          2) In Xero user interface click the `X` icon next to the invoice
          allocation line.
        </div>
        <div>3) Then come back to Colab and try to deallocate again.</div>
        <div>List of allocated credit notes in Xero:</div>
        <div>
          <ul>
            {warnings.map(warning => (
              <li key={warning.xeroNumber}>
                <div>
                  <ExternalLink href={warning.link} targetBlank>
                    {warning.xeroNumber}
                  </ExternalLink>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );

    return this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.DEALLOCATE_CREDIT_NOTE
      },
      () =>
        this.openConfirmationModal({
          heading: 'Deallocate credit notes',
          warnings: !_.isEmpty(warnings) && [modal_warnings],
          question: 'Are you sure you want to deallocate the CreditNotes?',
          confirmAction: this.deallocateCreditNotesConfirmAction
        })
    );
  };

  addXeroIdToInvoice = () =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.ADD_XERO_ID
      },
      () => {
        const xeroId = window.prompt('Xero ID: ', '');
        if (!_.isEmpty(xeroId)) {
          const successExtraActions = [
            ...this.getSuccessExtraActions(),
            this.delayedFetchScheduleAction
          ];
          const failureExtraActions = this.props.storeDataExtraActions(
            ({errors}) => ({errors})
          );

          this.props.addXeroId(
            {
              xeroId,
              objectId: this.props.data.invoiceId,
              objectType: SCHEDULE_ITEM_TYPES.INVOICE,
              scheduleType: this.props.data.scheduleType,
              tenancyId: this.props.data.tenancyId
            },
            successExtraActions,
            failureExtraActions
          );
        }
      }
    );

  openCreateCreditNoteModal = () =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.CREATE_CREDIT_NOTE
      },
      () => {
        const {scheduleType, invoice, locationRef, tenancyId} = this.props.data;

        this.props.closeModal();

        this.props.openModal(MODALS.CREDIT_NOTE_CREATE, {
          scheduleType,
          invoice,
          locationRef,
          tenancyId,
          primaryTenant: _.get(invoice, 'tenant')
        });
      }
    );

  openPaymentDetailModal = paymentId =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.VIEW_SCHEDULE_ITEM_DETAILS
      },
      () => {
        const {scheduleType, locationRef, tenancyId} = this.props.data;

        this.props.composeActions([
          () => closeModal(MODALS.INVOICE_DETAIL),
          () =>
            openModal(
              MODALS.PAYMENT_DETAIL,
              {
                scheduleType,
                paymentId,
                locationRef,
                tenancyId
              },
              true
            )
        ]);
      }
    );

  openCreditNoteDetailModal = creditNoteId =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.VIEW_SCHEDULE_ITEM_DETAILS
      },
      () => {
        const {scheduleType, locationRef, tenancyId} = this.props.data;

        this.props.composeActions([
          () => closeModal(MODALS.INVOICE_DETAIL),
          () =>
            openModal(
              MODALS.CREDIT_NOTE_DETAIL,
              {
                scheduleType,
                creditNoteId,
                locationRef,
                tenancyId
              },
              true
            )
        ]);
      }
    );

  openGocardlessPaymentDetailModal = gocardlessPaymentId => {
    const {scheduleType, tenancyId, locationRef} = this.props.data;

    this.props.composeActions([
      () => closeModal(MODALS.INVOICE_DETAIL),
      () =>
        openModal(
          MODALS.GOCARDLESS_PAYMENT_DETAIL,
          {
            gocardlessPaymentId,
            scheduleType,
            tenancyId,
            locationRef
          },
          true
        )
    ]);
  };

  syncWithXeroConfirmAction = () => {
    const successExtraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      ...this.getSuccessExtraActions(),
      this.delayedFetchScheduleAction
    ];
    const failureExtraAction = [
      ({errors}) =>
        storeDataInModal({
          modalName: BASE_MODALS.CONFIRM,
          data: {errors, submitting: false}
        })
    ];

    this.props.syncWithXero(
      {
        tenancyId: this.props.data.tenancyId,
        objectId: this.props.data.invoiceId,
        objectType: SCHEDULE_ITEM_TYPES.INVOICE,
        scheduleType: this.props.data.scheduleType
      },
      successExtraActions,
      failureExtraAction
    );
  };

  syncInvoiceWithXero = () =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.SYNC_WITH_XERO
      },
      () =>
        this.openConfirmationModal({
          heading: 'Sync Invoice',
          question: 'Are you sure you want to sync this invoice with xero?',
          confirmAction: this.syncWithXeroConfirmAction,
          warnings: []
        })
    );

  initiateGocardlessPayment = () =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.INITIATE_GOCARDLESS_PAYMENT
      },
      () => {
        const {
          data: {
            tenancyId,
            invoiceId,
            scheduleType,
            invoice: {isDueDateInFuture, totalDueIncludingTax, description}
          }
        } = this.props;

        this.props.openModal(MODALS.INITIATE_GOCARDLESS_PAYMENT, {
          invoiceId,
          tenancyId,
          amount: totalDueIncludingTax,
          description,
          scheduleType,
          isDueDateInFuture,
          invoice: this.props.data.invoice
        });
      }
    );

  render() {
    const scheduleType = _.get(this.props, 'data.scheduleType');
    const loading = _.get(this.props, 'data.loading');
    const xeroAccounts = _.get(this.props, 'data.xeroAccounts', []);
    const invoice = _.get(this.props, 'data.invoice');
    const errors = _.get(this.props, 'data.errors', {});
    const locationRef = _.get(this.props, 'data.locationRef');
    const tenancyId = _.get(this.props, 'data.tenancyId');
    const canBeDeallocated = _.isEmpty(
      _.get(this.props, 'data.invoice.creditNotes')
    );
    const xeroSyncErrors = _.get(this.props, 'data.invoice.xeroErrors', []);

    let modalType = Modal.MEDIUM;
    if (invoice && scheduleType === SCHEDULE_TYPES.RENT) {
      modalType = Modal.LARGE;
    }
    return (
      <div>
        {invoice && (
          <Modal
            show={true}
            onClose={this.props.closeModal}
            type={modalType}
            className="InvoiceDetailModal">
            <Modal.Header>
              <h3 className="header">Invoice {invoice.idString}</h3>
              <div className="status">
                <InvoiceStatusTag
                  overdue={invoice.overdue}
                  status={invoice.status}
                />
              </div>
            </Modal.Header>
            <Modal.Body>
              <Modal.Errors errors={errors} />
              <Modal.Errors errors={xeroSyncErrors} />

              <BaseInformation
                invoice={invoice}
                scheduleType={scheduleType}
                voidInvoice={this.voidInvoice}
                addXeroIdToInvoice={this.addXeroIdToInvoice}
                openCreateCreditNoteModal={this.openCreateCreditNoteModal}
                syncInvoiceWithXero={this.syncInvoiceWithXero}
                initiateGocardlessPayment={this.initiateGocardlessPayment}
                deallocateCreditNotes={this.deallocateCreditNotes}
                canBeDeallocated={canBeDeallocated}
              />
              <InvoiceItemsTable invoice={invoice} />
              <Totals invoice={invoice} />
              {loading && (
                <div className="loaderContainer">
                  <Loader />
                </div>
              )}
              <LessSection
                creditNotes={invoice.creditNotes}
                openCreditNoteDetailModal={this.openCreditNoteDetailModal}
                payments={invoice.payments}
                openPaymentDetailModal={this.openPaymentDetailModal}
                openGocardlessPaymentDetailModal={
                  this.openGocardlessPaymentDetailModal
                }
              />
              <AmountDue amountDue={invoice.totalDueIncludingTax} />
              {invoice.totalDueIncludingTax > 0 && (
                <VisibleIf
                  permission={PERMISSIONS.ADD_PAYMENT}
                  locationRef={locationRef}>
                  <ReceivePayment
                    tenancyId={tenancyId}
                    delayExtraActions={this.props.delayExtraActions}
                    invoice={invoice}
                    getExtraActions={this.getSuccessExtraActions}
                    xeroAccounts={xeroAccounts}
                    scheduleType={scheduleType}
                    openConfirmPaymentModal={this.props.openConfirmPaymentModal}
                    storeDataExtraActions={this.props.storeDataExtraActions}
                    locationRef={locationRef}
                  />
                </VisibleIf>
              )}
            </Modal.Body>
          </Modal>
        )}
        {!invoice && null}
      </div>
    );
  }
}

const mapStateToModalData = (_state, data) => {
  return {warnings: _.get(data, 'invoiceAllocationWarnings')};
};

const mapDispatchToProps = {
  fetchInvoice,
  voidObject,
  addXeroId,
  syncWithXero,
  fetchXeroAccounts,
  requiresPermission,
  delayedAction,
  openModal,
  deallocateCreditNotes,
  fetchInvoiceAllocatedCreditNotesXeroLinks,
  composeActions
};

export default withModal(
  MODALS.INVOICE_DETAIL,
  mapStateToModalData,
  mapDispatchToProps
)(InvoiceDetailModal);
